// src/tabs/logements/CreateCategoryModal.jsx — ADR-171
//
// Modal de création de catégorie livret. Extraite de l'ancien
// LivretCategoriesEditor.jsx (ADR-166, supprimé par ADR-171 au profit
// d'une édition intégrée dans la grille admin).
//
// Backend : livret_category_create (5d, ADR-166, requireAdmin Bearer JWT).
// Validation client mirror serveur (regex slug + max length labels).
//
// a11y :
//   - role=dialog + aria-modal=true + aria-label
//   - Escape handler ferme la modal
//   - Click overlay (hors form) ferme la modal
//   - autoFocus sur l'input label FR

import { useState, useEffect } from "react";
import { DS } from "../../lib/ui";

// ── Helpers (mirror serveur regex `/^[a-z][a-z0-9_]{1,29}$/`) ───────────────
export const SLUG_REGEX = /^[a-z][a-z0-9_]{1,29}$/;
export const MAX_LABEL_LEN = 60;

export function slugifyLabel(label) {
  if (typeof label !== "string") return "";
  return label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // accents
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_")
    .slice(0, 30);
}

// ── Composant principal ─────────────────────────────────────────────────────

export default function CreateCategoryModal({ existingSlugs, onClose, onSubmit }) {
  const [labelFr, setLabelFr] = useState("");
  const [labelEn, setLabelEn] = useState("");
  const [labelEs, setLabelEs] = useState("");
  const [slugManual, setSlugManual] = useState("");
  const [autoSlug, setAutoSlug] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);

  const slug = autoSlug ? slugifyLabel(labelFr) : slugManual.toLowerCase();
  const slugValid = SLUG_REGEX.test(slug);
  const slugConflict = (existingSlugs || []).includes(slug);

  // Escape handler
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function handleSubmit(e) {
    e.preventDefault();
    setLocalError(null);
    if (!labelFr.trim()) {
      setLocalError("Le label FR est obligatoire");
      return;
    }
    if (!slugValid) {
      setLocalError(
        "Slug invalide (lettre minuscule, lettres/chiffres/_, 2-30 caractères)",
      );
      return;
    }
    if (slugConflict) {
      setLocalError(`Le slug "${slug}" existe déjà pour ce logement`);
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        slug,
        label_fr: labelFr.trim(),
        label_en: labelEn.trim() || labelFr.trim(),
        label_es: labelEs.trim() || labelFr.trim(),
      });
    } catch (err) {
      if (err.error === "slug_already_exists") {
        setLocalError(`Le slug "${slug}" existe déjà pour ce logement`);
      } else if (err.error === "max_categories_reached") {
        setLocalError("Limite de 50 catégories par logement atteinte.");
      } else {
        setLocalError(err.message || "Erreur création catégorie");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Créer une catégorie"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(33,83,135,0.45)",
        zIndex: 9000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: DS.fontBody,
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="fbz-modal-sheet"
        style={{
          background: DS.white,
          borderRadius: 14,
          padding: 22,
          maxWidth: 500,
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
        }}
      >
        <h3
          style={{
            margin: "0 0 14px",
            fontFamily: DS.fontDisplay,
            color: DS.navy,
            fontSize: 18,
          }}
        >
          🗂️ Créer une catégorie
        </h3>

        {localError && (
          <div
            role="alert"
            style={{
              padding: 10,
              marginBottom: 12,
              background: "#fef2f2",
              border: `1px solid ${DS.charge}`,
              borderRadius: 7,
              color: DS.charge,
              fontSize: 12,
            }}
          >
            {localError}
          </div>
        )}

        <label style={_labelStyle()}>
          🇫🇷 Label FR (obligatoire)
          <input
            type="text"
            value={labelFr}
            onChange={(e) => setLabelFr(e.target.value)}
            maxLength={MAX_LABEL_LEN}
            required
            autoFocus
            placeholder="ex. Bons plans Toulouse"
            style={_inputStyle()}
          />
        </label>

        <label style={_labelStyle()}>
          🇬🇧 Label EN (optionnel, fallback FR)
          <input
            type="text"
            value={labelEn}
            onChange={(e) => setLabelEn(e.target.value)}
            maxLength={MAX_LABEL_LEN}
            placeholder="ex. Toulouse tips"
            style={_inputStyle()}
          />
        </label>

        <label style={_labelStyle()}>
          🇪🇸 Label ES (optionnel, fallback FR)
          <input
            type="text"
            value={labelEs}
            onChange={(e) => setLabelEs(e.target.value)}
            maxLength={MAX_LABEL_LEN}
            placeholder="ex. Consejos Toulouse"
            style={_inputStyle()}
          />
        </label>

        <div style={{ marginBottom: 14 }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12,
              color: "#444",
              marginBottom: 6,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={autoSlug}
              onChange={(e) => setAutoSlug(e.target.checked)}
            />
            Générer le slug automatiquement depuis le label FR
          </label>
          <label style={_labelStyle()}>
            Slug technique (lecture seule si auto)
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlugManual(e.target.value)}
              readOnly={autoSlug}
              maxLength={30}
              style={{
                ..._inputStyle(),
                background: autoSlug ? DS.pageBg : DS.white,
                fontFamily: "monospace",
                fontSize: 12,
              }}
            />
          </label>
          {slug && !slugValid && (
            <p style={{ fontSize: 11, color: DS.charge, margin: "4px 0 0" }}>
              Slug invalide — pattern : commence par lettre, lettres/chiffres/_ uniquement, 2-30 caractères
            </p>
          )}
          {slug && slugValid && slugConflict && (
            <p style={{ fontSize: 11, color: DS.charge, margin: "4px 0 0" }}>
              Slug déjà utilisé pour ce logement
            </p>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={_btnGhostStyle("#666", submitting)}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={submitting || !labelFr.trim() || !slugValid || slugConflict}
            style={_btnPrimaryStyle(
              DS.navy,
              submitting || !labelFr.trim() || !slugValid || slugConflict,
            )}
          >
            {submitting ? "⏳ Création…" : "✨ Créer la catégorie"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Styles partagés ────────────────────────────────────────────────────────
function _labelStyle() {
  return {
    display: "block",
    fontSize: 11,
    color: "#444",
    fontWeight: 600,
    marginBottom: 10,
    fontFamily: DS.fontBody,
  };
}

function _inputStyle() {
  return {
    display: "block",
    width: "100%",
    padding: "8px 10px",
    marginTop: 4,
    border: `1px solid ${DS.border}`,
    borderRadius: 7,
    fontSize: 13,
    fontFamily: DS.fontBody,
    color: "#222",
    boxSizing: "border-box",
  };
}

function _btnGhostStyle(color, disabled) {
  return {
    padding: "8px 14px",
    background: DS.white,
    color,
    border: `1px solid ${color}`,
    borderRadius: 7,
    cursor: disabled ? "wait" : "pointer",
    fontFamily: DS.fontBody,
    fontSize: 12,
    fontWeight: 600,
    minHeight: 44,
    opacity: disabled ? 0.6 : 1,
  };
}

function _btnPrimaryStyle(color, disabled) {
  return {
    padding: "8px 14px",
    background: disabled ? "#cbd5e1" : color,
    color: DS.white,
    border: "none",
    borderRadius: 7,
    cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: DS.fontBody,
    fontSize: 12,
    fontWeight: 700,
    minHeight: 44,
  };
}
